import {
  HirerNotificationPreferenceForm,
  useGetNotificationPreference,
  useSaveNotificationPreference,
  type GetHirerNotificationPreferenceResult,
  type NotificationPreferenceSaveEventData,
} from '@seek/hirer-notification-preference';
import { useTranslations } from '@vocab/react';
import { Notice, Text, TextLink } from 'braid-design-system';
import { useEffect, useState } from 'react';

import {
  type ActionResult,
  failResult,
  okResult,
  ActionableFormView,
} from 'src/components/common/ActionableFormView';
import { useAppConfig } from 'src/constants/AppConfig/AppConfigProvider';
import { EVENT_NAME_MANAGE_NOTIFICATIONS_SELF_SAVE_PRESSED } from 'src/constants/pageTrackingData';
import { trackEvent } from 'src/modules/events';
import { isANZSite } from 'src/utils/site';

import {
  getAnzCsPhoneNumberFromSite,
  getHelpCentreUrlFromSite,
} from '../../../utils/getCsContactMethodMessageFromSite.ts';

import translations from './.vocab/index.ts';

interface Props {
  onSubmitSuccess: () => void;
  onCancel: () => void;
}
export const NotificationPreferenceForm = ({
  onSubmitSuccess,
  onCancel,
}: Props) => {
  const { t } = useTranslations(translations);
  const { site, language } = useAppConfig();

  const saveNotificationPreference = useSaveNotificationPreference();
  const getNotificationPreference = useGetNotificationPreference();
  const [notificationPreferenceLoaded, setNotificationPreferenceLoaded] =
    useState<boolean>(false);
  const [hasLoadError, setHasLoadError] = useState<boolean>(false);

  useEffect(() => {
    if (!notificationPreferenceLoaded) {
      setHasLoadError(false);
      getNotificationPreference(
        (
          notificationPreferenceResult: GetHirerNotificationPreferenceResult,
        ) => {
          setNotificationPreferenceLoaded(true);
          setHasLoadError(notificationPreferenceResult.error ?? false);
        },
      );
    }
  }, [notificationPreferenceLoaded, getNotificationPreference]);

  const trackSaveEvent = (data: NotificationPreferenceSaveEventData) => {
    trackEvent(EVENT_NAME_MANAGE_NOTIFICATIONS_SELF_SAVE_PRESSED, data);
  };

  const csPhoneNumber = getAnzCsPhoneNumberFromSite(site);

  const onSave = async (): Promise<ActionResult> => {
    const saveResult = await saveNotificationPreference(trackSaveEvent);

    if (saveResult.success) {
      onSubmitSuccess();
      return okResult(t('Saved'));
    }
    const errorMessage = saveResult.error?.errorMessage ?? '';
    const isServerError = saveResult?.error?.isServerError ?? false;

    const noAccessToJobMessage = isANZSite(site)
      ? t(
          "Sorry, we can't save your changes right now. Can we try again later...",
          {
            TelLink: () => (
              <TextLink href={`tel:${csPhoneNumber}`}>{csPhoneNumber}</TextLink>
            ),
          },
        )
      : t(
          "Sorry, we can't save your changes right now. Can we try again later - with Customer Service link",
          {
            Link: (children) => (
              <TextLink href={getHelpCentreUrlFromSite(site, language)}>
                {children}
              </TextLink>
            ),
          },
        );

    return failResult(isServerError ? noAccessToJobMessage : errorMessage);
  };

  const loadErrorMessage = isANZSite(site)
    ? t("We can't show your notifications right now...", {
        TelLink: () => (
          <TextLink href={`tel:${csPhoneNumber}`}>{csPhoneNumber}</TextLink>
        ),
      })
    : t(
        "We can't show your notifications right now - with Customer Service link",
        {
          Link: (children) => (
            <TextLink href={getHelpCentreUrlFromSite(site, language)}>
              {children}
            </TextLink>
          ),
        },
      );

  return hasLoadError ? (
    <Notice tone="critical">
      <Text tone="critical">{loadErrorMessage}</Text>
    </Notice>
  ) : (
    <ActionableFormView
      onCancel={onCancel}
      onSubmit={onSave}
      submitButtonLabel={t('Save')}
      submitButtonLoadingLabel={t('Saving')}
    >
      <HirerNotificationPreferenceForm id="NotificationPreferenceForm" />
    </ActionableFormView>
  );
};
